// Common layout wrapper with navigation
import { Link, useLocation } from 'react-router-dom';

const Layout = ({ children }) => {
  const location = useLocation();
  
  // Check if we're in advanced mode
  const isAdvancedMode = location.pathname.startsWith('/advanced');
  
  // Helper function to determine if a nav link is active
  const isActive = (path) => {
    if (path === '/advanced') return location.pathname === '/advanced';
    return location.pathname.startsWith(path);
  };
  
  // Navigation link component
  const NavLink = ({ to, children }) => {
    const active = isActive(to);
    return (
      <Link
        to={to}
        className={`px-4 py-2 rounded-md ${
          active
            ? 'bg-blue-600 text-white'
            : 'text-gray-700 hover:bg-gray-100'
        }`}
      >
        {children}
      </Link>
    );
  };

  // Zen mode (simplified) layout
  if (!isAdvancedMode) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Minimal header for Zen mode */}
        <header className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-20">
              <div className="flex items-center">
                <div className="flex-shrink-0 flex items-center">
                  <img 
                    src="/images/logo1.png" 
                    alt="DMT Logo" 
                    className="h-16 w-auto mr-2 my-4" 
                  />
                </div>
              </div>
              <div className="flex items-center">
                <Link 
                  to="/advanced" 
                  className="ml-4 px-3 py-1 text-sm text-gray-700 hover:text-gray-900 hover:underline"
                >
                  Advanced Mode
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Main content for Zen mode */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {children}
        </main>

        {/* Simplified footer */}
        <footer className="bg-white border-t border-gray-200 mt-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="py-4 text-center text-sm text-gray-500">
              &copy; {new Date().getFullYear()} VShift
            </div>
          </div>
        </footer>
      </div>
    );
  }

  // Advanced mode layout (with full navigation)
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20">
            <div className="flex items-center">
              <Link to="/advanced" className="flex-shrink-0 flex items-center">
                <img 
                  src="/images/logo1.png" 
                  alt="DMT Logo" 
                  className="h-16 w-auto mr-2 my-4" 
                />
              </Link>
            </div>
            <div className="flex items-center">
              <Link 
                to="/" 
                className="ml-4 px-3 py-1 text-sm text-gray-700 hover:text-gray-900 hover:underline"
              >
        Basic Mode
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-4 py-3">
            <NavLink to="/advanced">Dashboard</NavLink>
            <NavLink to="/advanced/file-sets">File Sets</NavLink>
            <NavLink to="/advanced/scrape-jobs">Site Crawling</NavLink>
            <NavLink to="/advanced/analysis-jobs">Site Analysis</NavLink>
            <div className="flex-grow"></div>
            <NavLink to="/advanced/admin">Admin</NavLink>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 text-center text-sm text-gray-500">
            &copy; {new Date().getFullYear()} VShift
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;